import { downloadImages, getFinalDesignLicenseStatus } from './CommonUtils';
import { getDesignItems, getDesignLicenseStatus } from './DesignUtils';
import { getApparelImages, shouldUsePromoApparelImages } from './ApparelUtils';
import {
  DESIGN_TOOL_DISABLED_MESSAGE,
  MOB_DESIGN_TOOL_DISABLED_MESSAGE,
  ORIGINAL_VERSION,
  type DesignLicenseStatusTypes,
} from '@/constants/Proof';
import type {
  IDesignDoneResponse,
  IDesignProofItem,
  IDesignResponseData,
} from '@/common/interfaces/design.interface';
import type { MostRecentModel } from '@/common/interfaces/mostRecent.interface';
import { captureFileDownloaded } from '@/mixpanel/MixpanelEvents';
import { DESIGN_TOOL } from '@/constants/Config';
import { buildDesignToolUrlWithProofItemId } from '@/utilities/DesignToolUrlUtils';

/**
 * Method to retrieve the list of design done items.
 *
 * @param designsObj - Designs object coming from the designs API.
 */
export const getDesignDoneList = (designsObj: IDesignDoneResponse) => {
  const { proofs } = designsObj;
  const proofCardRecords: IDesignProofItem[] = [];

  proofs.forEach((proof) => {
    const proofRecord = {
      proof: {
        id: proof.id,
        name: proof.title,
        status: 0,
      },
      proofItems: proof.proofItems,
    };

    const proofItemRecords = getDesignItems(proofRecord, 'proofItemId');

    proofItemRecords.proofItemIds.forEach((proofItemId) => {
      proofItemRecords.groupedProofItems[proofItemId].forEach((proofItem: IDesignProofItem) => {
        proofItem.title = proofRecord.proof.name;
        proofItem.client = proof.client;
        proofItem.proof = {
          id: proof.id,
          name: proof.title,
        };
        proofItem.finalDesignLicenseStatus = getFinalDesignLicenseStatus(
          proofItem.designLicensing?.greek_status as DesignLicenseStatusTypes,
          proofItem.designLicensing?.collegiate_status as DesignLicenseStatusTypes
        ) as string;

        if (
          !proofItem.finalDesignLicenseStatus &&
          proofItem.designLicensing?.greek_status === 'no_marks' &&
          proofItem.designLicensing?.collegiate_status === 'no_marks'
        ) {
          proofItem.finalDesignLicenseStatus = 'no_marks';
        }

        if (proofItem.designLicensing) {
          proofItem.licensingInfo = getDesignLicenseStatus(proofItem.designLicensing);
        }

        /** If the status is in progress, we should fetch the apparel image from the API. */
        if (!proofItem.primaryImage) {
          if (!shouldUsePromoApparelImages(proofItem.variantSnapshot)) {
            const [frontImage, backImage] = getApparelImages(
              proofItem.apparelStyleCode || '',
              proofItem.apparelHexCode || ''
            );

            proofItem.primaryImage = frontImage || backImage;
          }
        }

        proofCardRecords.push(proofItem);
      });
    });
  });

  /** Proof items are filtered based on whether they have external apparel on the front end because the revision numbers would be incorrect if I did it from the back end. */
  return proofCardRecords.filter((record) => !record.hasExternalApparel);
};

/**
 * Method to get the version number of the design.
 *
 * @param version - the version name of the design.
 */
export const getVersionNumber = (version: string, returnVersionNumber = false) => {
  if (returnVersionNumber) {
    if (version && version !== ORIGINAL_VERSION) {
      return Number(version.split(' ')[1]);
    }

    return null;
  }

  return version;
};

/**
 * @description - This function is used to download the design front and back images.
 *
 * @param {string} image - The image url to be downloaded.
 */
export const downloadDesignImage = async (image: string, data: MostRecentModel) => {
  const isImageDownloaded = await downloadImages(image);

  if (isImageDownloaded) {
    captureFileDownloaded(
      'Design',
      data.proofId as string,
      getVersionNumber(data.proofItemVersionName as string) as string
    );
  }
};

/* Function that set the proof status.
 *
 * @param proof - selected proof req data
 */
export const setProofStatus = (proofInfo: IDesignResponseData) => {
  let statusDesc: Array<string> = [];
  /**
   * The status of the proof request depends on the status of proof items the proof request has,
   * so we are using bitwise operator to get the proof request based on statusses.
   */
  if (proofInfo.proof.status >= 8) {
    statusDesc = ['Done'];

    proofInfo.proofItems.forEach((proofItem) => {
      if (proofItem.status === 1 && !statusDesc.includes('In Progress')) {
        statusDesc.push('In Progress');
      }
    });

    /* eslint-disable */
    if (
      (proofInfo.proof.status & 1 ||
        proofInfo.proof.status & 2 ||
        proofInfo.proof.status & 4 ||
        proofInfo.proof.status === 0) &&
      !statusDesc.includes('In Progress')
    ) {
      statusDesc.push('In Progress');
    }
  } else {
    statusDesc = ['In Progress'];
  }

  return statusDesc;
};

/**
 * Method to navigate to the design tool.
 *
 * @param proofItemId - The proof item id to navigate to the design tool.
 * @param isCorporateAccount - When true, URL includes `clientType=corporate` before `proofItemId`.
 */
export const navigateToDesignTool = (
  proofItemId: number,
  redirectToWithUTM: (targetUrl: string) => void,
  isCorporateAccount = false
) => {
  redirectToWithUTM(
    buildDesignToolUrlWithProofItemId(DESIGN_TOOL, proofItemId, isCorporateAccount)
  );
};

/**
 * Method to return the design tool tooltip.
 *
 * @param isMobile - Whether the device is mobile or not.
 * @param isProofInProgress - Whether the proof is in progress or not.
 * @param enableDesignToolBtn - Whether the design tool button is enabled or not.
 * @param showRevisionDesignToolOverlay - Whether the revision request button is shown or not.
 */
export const getDesignToolTooltop = (
  isMobile: boolean,
  isProofInProgress: boolean,
  enableDesignToolBtn: boolean,
  showRevisionDesignToolOverlay: boolean
) => {
  if (!isProofInProgress && !showRevisionDesignToolOverlay) {
    return enableDesignToolBtn
      ? 'Make the edits yourself.'
      : "Sorry! This design can't be edited in the Design Tool yet. Reach out to us in case If you need help.";
  } else {
    const disableMessage = isMobile
      ? MOB_DESIGN_TOOL_DISABLED_MESSAGE
      : DESIGN_TOOL_DISABLED_MESSAGE;
    return enableDesignToolBtn ? 'Make the edits yourself.' : disableMessage;
  }
};

/**
 * Method to return the minimum quantity for the design.
 *
 * @param isMto - Is MTO color.
 * @param isFlashEligible - Is flash eligible.
 */
export const getDesignMinQuantity = (isMto: boolean, isFlashEligible: boolean) => {
  if (isMto) {
    return 50;
  }

  if (isFlashEligible) {
    return 12;
  }

  return 12;
};
